import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { blogPosts, importedBlogComponents } from '../../data/blogs';
import { format } from 'date-fns';
import SEO from '../SEO';
import Contact from '../Contact'; // Import the Contact component

export default function BlogDetail() {
  const { slug } = useParams();
  const post = blogPosts.find(p => p.slug === slug);

  // Related posts - find posts with same category or tag
  const relatedPosts = post ? 
    blogPosts
      .filter(p => 
        p.slug !== post.slug && 
        (p.categories.some(c => post.categories.includes(c)) || 
         p.tags.some(t => post.tags.includes(t)))
      )
      .slice(0, 3) : 
    [];

  // Instead of dynamic import, use the importedBlogComponents registry
  const BlogContentComponent = post?.contentComponent 
    ? importedBlogComponents[post.contentComponent] 
    : null;

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, [slug, post]);

  if (!post) {
    return (
      <div className="mt-24 container px-4 max-w-7xl mx-auto py-24 text-center">
        <SEO 
          title="Blog Post Not Found | BetterByAI"
          description="The blog post you're looking for doesn't exist or may have been moved."
        />
        <h1 className="text-3xl md:text-4xl font-bold mb-6 text-primary">Blog Post Not Found</h1>
        <p className="text-lg text-secondary mb-8">The blog post you're looking for doesn't exist or may have been moved.</p>
        <Link to="/blog" className="inline-flex items-center bg-[#FF7066] text-white px-6 py-3 rounded-lg hover:bg-[#FF7066]/90 transition-colors">
          Back to Blog
        </Link>
      </div>
    );
  }

  // Schema markup for blog post
  const blogPostSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://betterbyai.com/blog/${post.slug}`
    },
    "headline": post.title,
    "description": post.excerpt,
    "image": post.coverImage.src,
    "author": {
      "@type": "Person",
      "name": post.author.name
    },
    "publisher": {
      "@type": "Organization",
      "name": "BetterByAI",
      "logo": {
        "@type": "ImageObject",
        "url": "https://betterbyai.com/logo.png"
      }
    },
    "datePublished": post.date,
    "dateModified": post.date
  };

  return (
    <div className="bg-white pt-24">
      <SEO 
        title={post.seo?.metaTitle || `${post.title} | BetterByAI Blog`}
        description={post.seo?.metaDescription || post.excerpt}
        canonical={`https://betterbyai.com/blog/${post.slug}`}
        schemaMarkup={[blogPostSchema]}
      />
      
      {/* Hero Section - simplified with no background */}
      <section className="relative pt-12 pb-16 overflow-hidden">
        <div className="container px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl mx-auto">
            {/* Back to blog */}
            <div className="mb-6">
              <Link 
                to="/blog" 
                className="inline-flex items-center text-sm text-[#FF7066] hover:text-[#FF7066]/80 transition-colors"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
                </svg>
                Back to all posts
              </Link>
            </div>
            
            {/* Categories */}
            <div className="flex flex-wrap gap-2 mb-4">
              {post.categories.map(category => (
                <Link
                  key={category}
                  to={`/blog?category=${encodeURIComponent(category)}`}
                  className="px-2 py-1 bg-[#FF7066]/10 text-[#FF7066] rounded-full text-xs font-medium hover:bg-[#FF7066]/20 transition-colors"
                >
                  {category}
                </Link>
              ))}
            </div>
            
            {/* Title */}
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-3xl sm:text-4xl md:text-5xl font-bold text-primary mb-6"
            >
              {post.title}
            </motion.h1>
            
            {/* Meta info - removed author image */}
            <div className="flex items-center justify-between mb-8 pb-6 border-b border-gray-100">
              {/* Author - simplified without image */}
              <div>
                <div className="font-medium text-primary">{post.author.name}</div>
                {post.author.title && (
                  <div className="text-sm text-gray-500">{post.author.title}</div>
                )}
              </div>
              
              {/* Date & Read Time */}
              <div className="text-right text-sm text-gray-500">
                <div>{format(new Date(post.date), 'MMMM dd, yyyy')}</div>
                <div>{post.readTime} min read</div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Featured Image */}
      <section className="mb-12">
        <div className="container px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto"> {/* Changed from max-w-2xl to max-w-3xl */}
            <div className="aspect-w-16 aspect-h-9 rounded-xl overflow-hidden shadow-md">
              <img 
                src={typeof post.coverImage.src === 'string' 
                  ? post.coverImage.src 
                  : post.coverImage.src.src}
                alt={post.coverImage.alt || post.title}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>
      
      {/* Blog Content */}
      <section className="pb-12">
        <div className="container px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            {BlogContentComponent && <BlogContentComponent />}
            
            {/* Tags */}
            <div className="mt-12 pt-6 border-t border-gray-100">
              <div className="flex flex-wrap gap-2">
                {post.tags.map(tag => (
                  <Link
                    key={tag}
                    to={`/blog?tag=${encodeURIComponent(tag)}`}
                    className="px-3 py-1 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-full text-sm font-medium transition-colors"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>
            </div>
            
            {/* Author Bio - removed avatar */}
            {post.author.bio && (
              <div className="mt-12 p-6 bg-gray-50 rounded-xl">
                <div>
                  <div className="font-bold text-lg text-primary">{post.author.name}</div>
                  {post.author.title && (
                    <div className="text-gray-600">{post.author.title}</div>
                  )}
                </div>
                <div className="mt-4 text-gray-600">{post.author.bio}</div>
              </div>
            )}
          </div>
        </div>
      </section>
      
      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="py-12 bg-gray-50">
          <div className="container px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-primary mb-8 text-center">Related Posts</h2>
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {relatedPosts.map(relatedPost => (
                <Link
                  key={relatedPost.slug}
                  to={`/blog/${relatedPost.slug}`}
                  className="group block bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300"
                >
                  <div className="h-48 overflow-hidden">
                    <img
                      src={relatedPost.coverImage.src}
                      alt={relatedPost.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-5">
                    <h3 className="font-bold text-primary group-hover:text-[#FF7066] transition-colors line-clamp-2">
                      {relatedPost.title}
                    </h3>
                    <div className="mt-2 text-xs text-gray-500">
                      {format(new Date(relatedPost.date), 'MMM dd, yyyy')}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
      
      {/* Contact Component instead of CTA */}
      <Contact />
    </div>
  );
}